<!-- Breadcrumb -->
<div class="breadcrumb-card mb-25 d-md-flex align-items-center justify-content-between">
    <h5 class="mb-0">Location Mapping</h5>
    <ol class="breadcrumb list-unstyled mt-0 mb-0 pl-0">
        <li class="breadcrumb-item position-relative">
            <a routerLink="/dashboard" class="d-inline-block position-relative">
                <i class="ri-home-8-line"></i> Dashboard
            </a>
        </li>
        <li class="breadcrumb-item position-relative">Settings</li>
        <li class="breadcrumb-item position-relative">Location Mapping</li>
    </ol>
</div>

<!-- Stat cards -->
<div class="lm-stats-row mb-25" *ngIf="syncPreview">
    <div class="lm-stat-card" [class.stat-good]="activeMappingCount > 0"
        matTooltip="Mappings that are fully configured and currently syncing stock between Linnworks and ShipEarly."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ activeMappingCount }}</span>
        <span class="lm-stat-label">Active</span>
    </div>
    <div class="lm-stat-card" [class.stat-combo]="combinationCount > 0"
        matTooltip="Active mappings where multiple Linnworks locations contribute combined stock to a single ShipEarly warehouse."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ combinationCount }}</span>
        <span class="lm-stat-label">Combination</span>
    </div>
    <div class="lm-stat-card" [class.stat-warn]="pendingMappingCount > 0"
        matTooltip="Mappings that are incomplete — either the Linnworks location or ShipEarly warehouse has not been assigned yet. Action required before sync can run."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ pendingMappingCount }}</span>
        <span class="lm-stat-label">Pending</span>
    </div>
    <div class="lm-stat-card"
        matTooltip="Locations deliberately excluded from sync. No alerts will fire for these. Use this for warehouses you intentionally do not want to map."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ ignoredCount }}</span>
        <span class="lm-stat-label">Ignored</span>
    </div>
    <div class="lm-stat-card" [class.stat-warn]="unresolvedAlertCount > 0"
        matTooltip="Unresolved alerts — locations that have gone missing from Linnworks or ShipEarly since the mapping was created. Review the Alerts tab to fix or dismiss them."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ unresolvedAlertCount }}</span>
        <span class="lm-stat-label">Alerts</span>
    </div>
    <div *ngIf="showSyncStatus" class="lm-stat-card"
        [class.stat-good]="syncPreview.ready_to_sync"
        [class.stat-error]="!syncPreview.ready_to_sync"
        matTooltip="Whether the sync cron job can run right now. 'Ready' means at least one active mapping and one active SKU mapping exist. 'Not ready' means sync will be skipped on the next cron run."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ syncPreview.ready_to_sync ? '✓ Ready' : '✗ Not ready' }}</span>
        <span class="lm-stat-label">Sync Status</span>
    </div>
    <div class="lm-stat-card" *ngIf="syncPreview.ready_to_sync"
        matTooltip="Estimated number of stock-update API calls ShipEarly will make on the next scheduled sync run, based on active mappings × active SKUs."
        matTooltipPosition="below">
        <span class="lm-stat-value">{{ syncPreview.estimated_sync_operations | number }}</span>
        <span class="lm-stat-label">Est. ops/run</span>
    </div>
</div>

<!-- Combination info banner
     Uses the safe getter `combinationWarehouseNames` (string[]) — never undefined -->
<div class="lm-info-banner mb-25" *ngIf="combinationWarehouseNames.length > 0">
    <i class="material-symbols-outlined lm-info-icon">info</i>
    <span>
        <strong>Combination mapping active:</strong>
        {{ combinationWarehouseNames.join(', ') }} —
        stock from multiple Linnworks locations is summed before pushing to these SE warehouses.
    </span>
</div>

<mat-card class="daxa-card recent-orders-card mb-25 border-radius bg-white border-none d-block"
    [class.rtl-enabled]="themeService.isRTLEnabled()">
    <mat-card-content>
        <mat-tab-group mat-stretch-tabs="false" mat-align-tabs="start"
            [selectedIndex]="selectedTabIndex"
            (selectedIndexChange)="selectedTabIndex = $event">

            <!-- ══════════════════════════════
                 TAB 1 — All Mappings
            ══════════════════════════════ -->
            <mat-tab>
                <ng-template mat-tab-label>
                    All Mappings
                    <span class="lm-tab-badge" *ngIf="pagination?.totalItems">{{ pagination?.totalItems }}</span>
                </ng-template>

                <mat-card class="scroll-container" style="padding: 2rem;">
                    <mat-card-header>
                        <mat-card-title>
                            <div class="lm-filter-group">
                                <span class="filter-label">Show:</span>
                                <button type="button" mat-stroked-button
                                    [class.active-filter]="filterStatus==='all'"
                                    (click)="setFilter('all')">All</button>
                                <button type="button" mat-stroked-button
                                    [class.active-filter]="filterStatus==='active'"
                                    (click)="setFilter('active')">Active</button>
                                <button type="button" mat-stroked-button
                                    [class.active-filter]="filterStatus==='inactive'"
                                    (click)="setFilter('inactive')">Pending</button>
                                <button type="button" mat-stroked-button
                                    [class.active-filter]="filterStatus==='ignored'"
                                    (click)="setFilter('ignored')">Ignored</button>
                            </div>
                        </mat-card-title>
                        <mat-card-subtitle>
                            <div class="d-flex justify-content-end">
                                <button type="button" mat-flat-button
                                    class="daxa card-header-menu-btn button-gap"
                                    (click)="loadAll()" [disabled]="loading">Refresh</button>
                                <button type="button" mat-flat-button
                                    class="daxa card-header-menu-btn"
                                    (click)="openCreateForm()">+ New Mapping</button>
                            </div>
                        </mat-card-subtitle>
                    </mat-card-header>

                    <mat-card-content>

                        <!-- Create form -->
                        <!-- Scroll target for openCreateForm() -->
                        <div #createFormAnchor></div>

                        <mat-expansion-panel class="lm-create-panel mb-20"
                            [expanded]="showCreateForm"
                            (closed)="showCreateForm = false"
                            *ngIf="showCreateForm">
                            <mat-expansion-panel-header>
                                <mat-panel-title>New Location Mapping</mat-panel-title>
                            </mat-expansion-panel-header>

                            <div class="lm-form-loading" *ngIf="liveDataLoading">
                                <mat-spinner diameter="20"></mat-spinner>&nbsp;Loading live API data…
                            </div>

                            <div *ngIf="!liveDataLoading">
                                <div class="lm-form-row">
                                    <mat-form-field appearance="outline" class="lm-form-field">
                                        <mat-label>Linnworks Location *</mat-label>
                                        <mat-select (selectionChange)="onCreateLwSelectMat($event)"
                                            [value]="createForm.lw_location_id">
                                            <mat-option value="">— Select LW location —</mat-option>
                                            <mat-option *ngFor="let loc of lwLocations" [value]="loc.id">
                                                {{ loc.name }}
                                            </mat-option>
                                        </mat-select>
                                        <mat-hint *ngIf="createForm.lw_location_id">
                                            ID: {{ createForm.lw_location_id }}
                                        </mat-hint>
                                    </mat-form-field>

                                    <span class="lm-arrow">→</span>

                                    <mat-form-field appearance="outline" class="lm-form-field">
                                        <mat-label>ShipEarly Warehouse *</mat-label>
                                        <mat-select (selectionChange)="onCreateSeSelectMat($event)"
                                            [value]="createForm.se_location_id">
                                            <mat-option value="">— Select SE warehouse —</mat-option>
                                            <mat-option *ngFor="let wh of seWarehouses" [value]="wh.id">
                                                {{ wh.name }}
                                            </mat-option>
                                        </mat-select>
                                        <mat-hint *ngIf="createForm.se_location_id">
                                            ID: {{ createForm.se_location_id }}
                                        </mat-hint>
                                    </mat-form-field>
                                </div>

                                <div class="lm-form-row lm-form-row-checks">
                                    <mat-form-field appearance="outline" class="lm-form-field">
                                        <mat-label>Notes (optional)</mat-label>
                                        <input matInput [(ngModel)]="createForm.notes"
                                            placeholder="e.g. Part of UK combination" />
                                    </mat-form-field>
                                    <div class="lm-form-checks">
                                        <mat-checkbox [(ngModel)]="createForm.is_combination">
                                            Combination mapping
                                        </mat-checkbox>
                                        <span class="hint-text" *ngIf="createForm.is_combination">
                                            Check if this LW location is one of several contributing to the same SE warehouse.
                                        </span>
                                        <mat-checkbox [(ngModel)]="createForm.activate">
                                            Activate immediately
                                        </mat-checkbox>
                                    </div>
                                </div>

                                <div class="combo-note" *ngIf="createForm.is_combination">
                                    <i class="material-symbols-outlined" style="font-size:1rem;vertical-align:middle">link</i>
                                    <strong>Combination mode:</strong> Create one row per contributing LW location,
                                    all pointing to the same SE warehouse. The cron will sum stock across all of them.
                                </div>

                                <div class="lm-form-actions">
                                    <button type="button" mat-stroked-button
                                        (click)="showCreateForm = false">Cancel</button>
                                    <button type="button" mat-flat-button
                                        class="daxa card-header-menu-btn"
                                        (click)="submitCreate()" [disabled]="saving">
                                        {{ saving ? 'Saving…' : 'Create Mapping' }}
                                    </button>
                                </div>
                            </div>
                        </mat-expansion-panel>

                        <!-- Mappings table -->
                        <div class="recent-orders-table">
                            <ng-container *ngIf="filteredMappings.length > 0; else noMappings">
                                <div class="table-responsive">
                                    <table mat-table [dataSource]="mappingsDataSource">

                                        <ng-container matColumnDef="lw_location">
                                            <th mat-header-cell *matHeaderCellDef>Linnworks Location</th>
                                            <td mat-cell *matCellDef="let m">
                                                <ng-container *ngIf="editingId !== m.id">
                                                    <div class="location-cell">
                                                        <span class="location-name">{{ m.lw_location_name || '—' }}</span>
                                                        <span class="location-id" *ngIf="m.lw_location_id">{{ m.lw_location_id }}</span>
                                                    </div>
                                                </ng-container>
                                                <ng-container *ngIf="editingId === m.id">
                                                    <mat-form-field appearance="outline" class="lm-inline-field">
                                                        <mat-select (selectionChange)="onEditLwSelectMat($event)"
                                                            [value]="editForm.lw_location_id">
                                                            <mat-option value="">— Select —</mat-option>
                                                            <mat-option *ngFor="let loc of lwLocations" [value]="loc.id">
                                                                {{ loc.name }}
                                                            </mat-option>
                                                        </mat-select>
                                                    </mat-form-field>
                                                </ng-container>
                                            </td>
                                        </ng-container>

                                        <ng-container matColumnDef="arrow">
                                            <th mat-header-cell *matHeaderCellDef></th>
                                            <td mat-cell *matCellDef="let m" class="arrow-cell">↔</td>
                                        </ng-container>

                                        <ng-container matColumnDef="se_location">
                                            <th mat-header-cell *matHeaderCellDef>ShipEarly Warehouse</th>
                                            <td mat-cell *matCellDef="let m">
                                                <ng-container *ngIf="editingId !== m.id">
                                                    <div class="location-cell">
                                                        <span class="location-name">{{ m.se_location_name || '—' }}</span>
                                                        <span class="location-id" *ngIf="m.se_location_id">ID: {{ m.se_location_id }}</span>
                                                    </div>
                                                </ng-container>
                                                <ng-container *ngIf="editingId === m.id">
                                                    <mat-form-field appearance="outline" class="lm-inline-field">
                                                        <mat-select (selectionChange)="onEditSeSelectMat($event)"
                                                            [value]="editForm.se_location_id">
                                                            <mat-option value="">— Select —</mat-option>
                                                            <mat-option *ngFor="let wh of seWarehouses" [value]="wh.id">
                                                                {{ wh.name }}
                                                            </mat-option>
                                                        </mat-select>
                                                    </mat-form-field>
                                                </ng-container>
                                            </td>
                                        </ng-container>

                                        <ng-container matColumnDef="status">
                                            <th mat-header-cell *matHeaderCellDef>Status</th>
                                            <td mat-cell *matCellDef="let m">
                                                <span class="lm-badge" [ngClass]="getStatusClass(m)">
                                                    {{ getStatusLabel(m) }}
                                                </span>
                                            </td>
                                        </ng-container>

                                        <ng-container matColumnDef="type">
                                            <th mat-header-cell *matHeaderCellDef>Type</th>
                                            <td mat-cell *matCellDef="let m">
                                                <ng-container *ngIf="editingId !== m.id">
                                                    <span class="type-tag" *ngIf="m.is_combination"
                                                        title="Stock is combined with other LW locations before pushing to SE">
                                                        🔗 Combination
                                                    </span>
                                                    <span class="type-tag-standard" *ngIf="!m.is_combination">1-to-1</span>
                                                </ng-container>
                                                <ng-container *ngIf="editingId === m.id">
                                                    <mat-checkbox [(ngModel)]="editForm.is_combination">
                                                        Combination
                                                    </mat-checkbox>
                                                </ng-container>
                                            </td>
                                        </ng-container>

                                        <ng-container matColumnDef="source">
                                            <th mat-header-cell *matHeaderCellDef>Source</th>
                                            <td mat-cell *matCellDef="let m">
                                                <ng-container *ngIf="editingId !== m.id">
                                                    <span class="source-tag">{{ m.mapping_source || 'manual' }}</span>
                                                </ng-container>
                                                <ng-container *ngIf="editingId === m.id">
                                                    <mat-form-field appearance="outline" class="lm-inline-field">
                                                        <mat-label>Notes</mat-label>
                                                        <input matInput [(ngModel)]="editForm.notes" placeholder="Notes…" />
                                                    </mat-form-field>
                                                </ng-container>
                                            </td>
                                        </ng-container>

                                        <ng-container matColumnDef="actions">
                                            <th mat-header-cell *matHeaderCellDef>Actions</th>
                                            <td mat-cell *matCellDef="let m">
                                                <!-- View mode -->
                                                <div class="action-info" *ngIf="editingId !== m.id">
                                                    <button type="button" mat-button
                                                        *ngIf="!m.is_active && !m.ignored && m.lw_location_id && m.se_location_id"
                                                        matTooltip="Activate" matTooltipPosition="above"
                                                        (click)="activate(m)">
                                                        <i class="material-symbols-outlined" style="color:#4caf50">check_circle</i>
                                                    </button>
                                                    <button type="button" mat-button
                                                        *ngIf="m.is_active"
                                                        matTooltip="Pause sync" matTooltipPosition="above"
                                                        (click)="deactivate(m)">
                                                        <i class="material-symbols-outlined" style="color:#ff9800">pause_circle</i>
                                                    </button>
                                                    <button type="button" mat-button
                                                        matTooltip="Edit" matTooltipPosition="above"
                                                        (click)="startEdit(m)">
                                                        <i class="material-symbols-outlined edit-icon-color">edit</i>
                                                    </button>
                                                    <button type="button" mat-button
                                                        *ngIf="!m.ignored"
                                                        matTooltip="Ignore" matTooltipPosition="above"
                                                        (click)="ignore(m)">
                                                        <i class="material-symbols-outlined" style="color:#9e9e9e">block</i>
                                                    </button>
                                                    <button type="button" mat-button
                                                        *ngIf="m.ignored"
                                                        matTooltip="Restore to pending" matTooltipPosition="above"
                                                        (click)="unignore(m)">
                                                        <i class="material-symbols-outlined" style="color:#2196f3">restore</i>
                                                    </button>
                                                    <button type="button" mat-button
                                                        matTooltip="Change history" matTooltipPosition="above"
                                                        (click)="viewAudit(m)">
                                                        <i class="material-symbols-outlined" style="color:#607d8b">history</i>
                                                    </button>
                                                </div>
                                                <!-- Edit mode -->
                                                <div class="action-info" *ngIf="editingId === m.id">
                                                    <button type="button" mat-flat-button
                                                        class="daxa card-header-menu-btn button-gap"
                                                        (click)="saveEdit()" [disabled]="saving">
                                                        {{ saving ? 'Saving…' : 'Save' }}
                                                    </button>
                                                    <button type="button" mat-stroked-button
                                                        (click)="cancelEdit()">Cancel</button>
                                                </div>
                                            </td>
                                        </ng-container>

                                        <tr mat-header-row *matHeaderRowDef="mappingDisplayedColumns"></tr>
                                        <tr mat-row *matRowDef="let row; columns: mappingDisplayedColumns;"
                                            [class.row-active]="row.is_active && !row.ignored"
                                            [class.row-combo]="row.is_combination && row.is_active"
                                            [class.row-ignored]="row.ignored"
                                            [class.row-editing]="editingId === row.id"></tr>
                                    </table>
                                </div>

                                <!-- Pagination -->
                                <div class="custom-paginator" *ngIf="pagination?.totalPages > 1">
                                    <button mat-icon-button
                                        [disabled]="!pagination?.canViewPreviousPage"
                                        (click)="changePage(page - 1)">
                                        <mat-icon>chevron_left</mat-icon>
                                    </button>
                                    <span class="paginator-text">
                                        Page {{ page }} of {{ pagination?.totalPages }}
                                        ({{ pagination?.totalItems }} total)
                                    </span>
                                    <button mat-icon-button
                                        [disabled]="!pagination?.canViewNextPage"
                                        (click)="changePage(page + 1)">
                                        <mat-icon>chevron_right</mat-icon>
                                    </button>
                                </div>

                            </ng-container>
                            <ng-template #noMappings>
                                <hr>
                                <div class="no-data-found text-center"><h5>No Mappings Found</h5></div>
                                <hr>
                            </ng-template>
                        </div>

                    </mat-card-content>
                </mat-card>
            </mat-tab>

            <!-- ══════════════════════════════
                 TAB 2 — Needs Mapping
            ══════════════════════════════ -->
            <mat-tab>
                <ng-template mat-tab-label>
                    Needs Mapping
                    <span class="lm-tab-badge lm-tab-badge-warn"
                        *ngIf="totalUnmapped > 0">
                        {{ totalUnmapped }}
                    </span>
                </ng-template>

                <mat-card class="scroll-container" style="padding: 2rem;">
                    <mat-card-content>

                        <div *ngIf="unmappedLw.length > 0" class="mb-25">
                            <h6 class="lm-section-title">
                                Linnworks Locations — No ShipEarly Warehouse Assigned
                                <span class="lm-badge badge-inactive ml-8">{{ unmappedLw.length }}</span>
                            </h6>
                            <p class="lm-section-desc">Click "Map →" to open the create form pre-filled with this LW location.</p>
                            <div class="table-responsive">
                                <table mat-table [dataSource]="unmappedLw">
                                    <ng-container matColumnDef="lw_name">
                                        <th mat-header-cell *matHeaderCellDef>LW Location Name</th>
                                        <td mat-cell *matCellDef="let row"><strong>{{ row.lw_location_name }}</strong></td>
                                    </ng-container>
                                    <ng-container matColumnDef="lw_id">
                                        <th mat-header-cell *matHeaderCellDef>LW Location ID</th>
                                        <td mat-cell *matCellDef="let row">{{ row.lw_location_id }}</td>
                                    </ng-container>
                                    <ng-container matColumnDef="lw_detected">
                                        <th mat-header-cell *matHeaderCellDef>Detected</th>
                                        <td mat-cell *matCellDef="let row">{{ row.created_at | date:'dd/MM/yyyy hh:mm a' }}</td>
                                    </ng-container>
                                    <ng-container matColumnDef="lw_action">
                                        <th mat-header-cell *matHeaderCellDef>Action</th>
                                        <td mat-cell *matCellDef="let row">
                                            <div class="action-info">
                                                <button type="button" mat-flat-button
                                                    class="daxa card-header-menu-btn button-gap"
                                                    (click)="openCreateForm(row, null)">Map →</button>
                                                <button type="button" mat-stroked-button
                                                    (click)="ignore(row)">Ignore</button>
                                            </div>
                                        </td>
                                    </ng-container>
                                    <tr mat-header-row *matHeaderRowDef="['lw_name','lw_id','lw_detected','lw_action']"></tr>
                                    <tr mat-row *matRowDef="let row; columns: ['lw_name','lw_id','lw_detected','lw_action'];"></tr>
                                </table>
                            </div>
                        </div>

                        <div *ngIf="unmappedSe.length > 0" class="mb-25">
                            <h6 class="lm-section-title">
                                ShipEarly Warehouses — No Linnworks Location Assigned
                                <span class="lm-badge badge-inactive ml-8">{{ unmappedSe.length }}</span>
                            </h6>
                            <p class="lm-section-desc">Click "Map →" to open the create form pre-filled with this SE warehouse.</p>
                            <div class="table-responsive">
                                <table mat-table [dataSource]="unmappedSe">
                                    <ng-container matColumnDef="se_name">
                                        <th mat-header-cell *matHeaderCellDef>SE Warehouse Name</th>
                                        <td mat-cell *matCellDef="let row"><strong>{{ row.se_location_name }}</strong></td>
                                    </ng-container>
                                    <ng-container matColumnDef="se_id">
                                        <th mat-header-cell *matHeaderCellDef>SE Warehouse ID</th>
                                        <td mat-cell *matCellDef="let row">{{ row.se_location_id }}</td>
                                    </ng-container>
                                    <ng-container matColumnDef="se_detected">
                                        <th mat-header-cell *matHeaderCellDef>Detected</th>
                                        <td mat-cell *matCellDef="let row">{{ row.created_at | date:'dd/MM/yyyy hh:mm a' }}</td>
                                    </ng-container>
                                    <ng-container matColumnDef="se_action">
                                        <th mat-header-cell *matHeaderCellDef>Action</th>
                                        <td mat-cell *matCellDef="let row">
                                            <button type="button" mat-flat-button
                                                class="daxa card-header-menu-btn"
                                                (click)="openCreateForm(null, row)">Map →</button>
                                        </td>
                                    </ng-container>
                                    <tr mat-header-row *matHeaderRowDef="['se_name','se_id','se_detected','se_action']"></tr>
                                    <tr mat-row *matRowDef="let row; columns: ['se_name','se_id','se_detected','se_action'];"></tr>
                                </table>
                            </div>
                        </div>

                        <div class="no-data-found text-center"
                            *ngIf="unmappedLw.length === 0 && unmappedSe.length === 0">
                            <hr><h5>✓ All locations are mapped.</h5><hr>
                        </div>

                    </mat-card-content>
                </mat-card>
            </mat-tab>

            <!-- ══════════════════════════════
                 TAB 3 — Alerts
            ══════════════════════════════ -->
            <mat-tab>
                <ng-template mat-tab-label>
                    Alerts
                    <span class="lm-tab-badge lm-tab-badge-error" *ngIf="unresolvedAlertCount > 0">
                        {{ unresolvedAlertCount }}
                    </span>
                </ng-template>

                <mat-card class="scroll-container" style="padding: 2rem;">
                    <mat-card-content>
                        <ng-container *ngIf="alerts.length > 0; else noAlerts">
                            <h6 class="lm-section-title">New Locations Detected</h6>
                            <p class="lm-section-desc">Discovered automatically by the cron. Map or ignore each one.</p>
                            <div class="table-responsive">
                                <table mat-table [dataSource]="alerts">
                                    <ng-container matColumnDef="side">
                                        <th mat-header-cell *matHeaderCellDef>Side</th>
                                        <td mat-cell *matCellDef="let a">
                                            <span class="lm-badge"
                                                [class.badge-lw]="a.side==='lw'"
                                                [class.badge-se]="a.side==='se'">
                                                {{ a.side.toUpperCase() }}
                                            </span>
                                        </td>
                                    </ng-container>
                                    <ng-container matColumnDef="location">
                                        <th mat-header-cell *matHeaderCellDef>Location / Warehouse</th>
                                        <td mat-cell *matCellDef="let a"><strong>{{ a.lw_location_name || a.se_location_name }}</strong></td>
                                    </ng-container>
                                    <ng-container matColumnDef="alert_id">
                                        <th mat-header-cell *matHeaderCellDef>ID</th>
                                        <td mat-cell *matCellDef="let a">{{ a.lw_location_id || a.se_location_id }}</td>
                                    </ng-container>
                                    <ng-container matColumnDef="detected_at">
                                        <th mat-header-cell *matHeaderCellDef>Detected</th>
                                        <td mat-cell *matCellDef="let a">{{ a.detected_at | date:'dd/MM/yyyy hh:mm a' }}</td>
                                    </ng-container>
                                    <ng-container matColumnDef="alert_actions">
                                        <th mat-header-cell *matHeaderCellDef>Actions</th>
                                        <td mat-cell *matCellDef="let a">
                                            <div class="action-info">
                                                <button type="button" mat-flat-button
                                                    class="daxa card-header-menu-btn button-gap"
                                                    (click)="openCreateForm(a.side==='lw' ? a : null, a.side==='se' ? a : null)">
                                                    Map Now
                                                </button>
                                                <button type="button" mat-stroked-button
                                                    (click)="resolveAlert(a)">Dismiss</button>
                                            </div>
                                        </td>
                                    </ng-container>
                                    <tr mat-header-row *matHeaderRowDef="['side','location','alert_id','detected_at','alert_actions']"></tr>
                                    <tr mat-row *matRowDef="let row; columns: ['side','location','alert_id','detected_at','alert_actions'];"></tr>
                                </table>
                            </div>
                        </ng-container>
                        <ng-template #noAlerts>
                            <hr><div class="no-data-found text-center"><h5>✓ No unresolved alerts.</h5></div><hr>
                        </ng-template>
                    </mat-card-content>
                </mat-card>
            </mat-tab>

            <!-- ══════════════════════════════
                 TAB 4 — Sync Preview
            ══════════════════════════════ -->
            <mat-tab  *ngIf="showSyncPreview" label="Sync Preview">
                <mat-card class="scroll-container" style="padding: 2rem;">
                    <mat-card-content>
                        <ng-container *ngIf="syncPreview">

                            <div class="lm-preview-status"
                                [class.preview-ready]="syncPreview.ready_to_sync"
                                [class.preview-not-ready]="!syncPreview.ready_to_sync">
                                <i class="material-symbols-outlined">
                                    {{ syncPreview.ready_to_sync ? 'check_circle' : 'cancel' }}
                                </i>
                                <span>{{ syncPreview.ready_to_sync
                                    ? 'Sync is ready to run'
                                    : 'Sync cannot run — no active mappings or SKU mappings' }}</span>
                            </div>

                            <div class="lm-preview-stats">
                                <div class="lm-preview-stat">
                                    <span class="lm-stat-value">{{ syncPreview.active_warehouse_count }}</span>
                                    <span class="lm-stat-label">Active SE warehouses</span>
                                </div>
                                <div class="lm-preview-stat">
                                    <span class="lm-stat-value">{{ syncPreview.active_sku_count }}</span>
                                    <span class="lm-stat-label">Active SKU mappings</span>
                                </div>
                                <div class="lm-preview-stat">
                                    <span class="lm-stat-value">{{ syncPreview.estimated_sync_operations | number }}</span>
                                    <span class="lm-stat-label">Est. API calls/run</span>
                                </div>
                                <div class="lm-preview-stat" *ngIf="syncPreview.pending_mapping_count > 0">
                                    <span class="lm-stat-value" style="color:#ff9800">{{ syncPreview.pending_mapping_count }}</span>
                                    <span class="lm-stat-label">Awaiting mapping</span>
                                </div>
                            </div>

                            <div *ngIf="syncPreview.will_sync.length > 0">
                                <h6 class="lm-section-title mb-20">Will Sync on Next Run</h6>
                                <div class="sync-groups">
                                    <mat-card class="sync-group-card"
                                        *ngFor="let grp of syncPreview.will_sync"
                                        [class.sync-group-combo]="grp.is_combination">
                                        <mat-card-header>
                                            <mat-card-title>{{ grp.se_location_name }}</mat-card-title>
                                            <mat-card-subtitle>
                                                <span class="lm-badge badge-combo" *ngIf="grp.is_combination">Combination</span>
                                                <span class="lm-badge badge-active" *ngIf="!grp.is_combination">1-to-1</span>
                                            </mat-card-subtitle>
                                        </mat-card-header>
                                        <mat-card-content>
                                            <div class="sync-lw-loc" *ngFor="let lw of grp.lw_locations">
                                                <i class="material-symbols-outlined" style="font-size:.9rem;color:#9e9e9e">arrow_upward</i>
                                                <span>{{ lw.name }}</span>
                                                <span class="location-id">{{ lw.id }}</span>
                                            </div>
                                            <p class="sync-note" *ngIf="grp.is_combination">
                                                Stock from all {{ grp.lw_locations.length }} LW locations summed → SE "{{ grp.se_location_name }}"
                                            </p>
                                        </mat-card-content>
                                    </mat-card>
                                </div>
                            </div>

                        </ng-container>
                    </mat-card-content>
                </mat-card>
            </mat-tab>

        </mat-tab-group>
    </mat-card-content>
</mat-card>

<!-- Audit Trail Modal -->
<div class="lm-modal-overlay" *ngIf="auditMapping" (click)="closeAudit()">
    <mat-card class="lm-audit-modal" (click)="$event.stopPropagation()">
        <mat-card-header>
            <mat-card-title>
                Change History — {{ auditMapping.lw_location_name || auditMapping.se_location_name }}
            </mat-card-title>
            <mat-card-subtitle>
                <button type="button" mat-icon-button class="lm-modal-close" (click)="closeAudit()">
                    <mat-icon>close</mat-icon>
                </button>
            </mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <ng-container *ngIf="auditRows.length > 0; else noAudit">
                <div class="table-responsive">
                    <table mat-table [dataSource]="auditRows">
                        <ng-container matColumnDef="action">
                            <th mat-header-cell *matHeaderCellDef>Action</th>
                            <td mat-cell *matCellDef="let a">
                                <span class="lm-badge">{{ a.action }}</span>
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="changed_by">
                            <th mat-header-cell *matHeaderCellDef>Changed By</th>
                            <td mat-cell *matCellDef="let a">{{ a.changed_by || '—' }}</td>
                        </ng-container>
                        <ng-container matColumnDef="changed_at">
                            <th mat-header-cell *matHeaderCellDef>When</th>
                            <td mat-cell *matCellDef="let a">{{ a.changed_at | date:'dd/MM/yyyy hh:mm a' }}</td>
                        </ng-container>
                        <ng-container matColumnDef="before">
                            <th mat-header-cell *matHeaderCellDef>Before</th>
                            <td mat-cell *matCellDef="let a">
                                <span *ngIf="a.old_lw_location_name || a.old_se_location_name">
                                    {{ a.old_lw_location_name }} ↔ {{ a.old_se_location_name }}
                                    <em class="d-block">active={{ a.old_is_active }}</em>
                                </span>
                                <span *ngIf="!a.old_lw_location_name && !a.old_se_location_name">—</span>
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="after">
                            <th mat-header-cell *matHeaderCellDef>After</th>
                            <td mat-cell *matCellDef="let a">
                                {{ a.new_lw_location_name }} ↔ {{ a.new_se_location_name }}
                                <em class="d-block">active={{ a.new_is_active }}</em>
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="notes">
                            <th mat-header-cell *matHeaderCellDef>Notes</th>
                            <td mat-cell *matCellDef="let a">{{ a.notes || '—' }}</td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="['action','changed_by','changed_at','before','after','notes']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['action','changed_by','changed_at','before','after','notes'];"></tr>
                    </table>
                </div>
            </ng-container>
            <ng-template #noAudit>
                <hr><div class="no-data-found text-center"><h5>No history recorded yet.</h5></div><hr>
            </ng-template>
        </mat-card-content>
    </mat-card>
</div>

<!-- Loader Overlay -->
<div *ngIf="loading" class="export-overlay">
    <div class="spinner"></div>
</div>